import { METER_IDENTIFIER_KINDS } from '@/shared/meter-identification';
import type { AiExtraction } from '@/shared/ocr/ai-extraction';
import { AI_EXTRACTION_JOB_TYPES, AI_EXTRACTION_STATUSES } from '@/shared/ocr/ai-extraction';

export const OCR_CANDIDATE_FIELDS = {
  apiKey: METER_IDENTIFIER_KINDS.apiKey,
  manufacturer: 'manufacturer',
  meterModel: 'meter_model',
  registerCounter: 'register_counter',
  registerObis: 'register_obis',
  registerUnit: 'register_unit',
  serial: METER_IDENTIFIER_KINDS.serial,
} as const;

export const ocrCandidateFields = Object.values(OCR_CANDIDATE_FIELDS);

export type OcrCandidateField = (typeof ocrCandidateFields)[number];

export interface OcrCandidate {
  confidence: number;
  field: OcrCandidateField;
  normalizedValue?: string;
  sourcePhotoId: string;
  value: string;
}

export interface OcrJobOutput {
  candidates: OcrCandidate[];
  requestedFields: OcrCandidateField[];
}

export function createQueuedOcrJob(input: {
  createdAt: string;
  id: string;
  photoId: string;
  requestedFields: OcrCandidateField[];
  sessionId: string;
}): AiExtraction {
  const output: OcrJobOutput = {
    candidates: [],
    requestedFields: input.requestedFields,
  };

  return {
    createdAt: input.createdAt,
    id: input.id,
    jobType: AI_EXTRACTION_JOB_TYPES.clientOcr,
    output: output as unknown as Record<string, unknown>,
    photoId: input.photoId,
    sessionId: input.sessionId,
    status: AI_EXTRACTION_STATUSES.queued,
  };
}

export function readOcrCandidates(extractions: AiExtraction[], field: OcrCandidateField): OcrCandidate[] {
  return extractions
    .filter(extraction => extraction.jobType === AI_EXTRACTION_JOB_TYPES.clientOcr)
    .flatMap(extraction => readCandidates(extraction.output))
    .filter(candidate => candidate.field === field)
    .toSorted((left, right) => right.confidence - left.confidence);
}

function readCandidates(output: Record<string, unknown>): OcrCandidate[] {
  const candidates = Array.isArray(output.candidates) ? output.candidates : [];

  return candidates.filter(isOcrCandidate);
}

function isOcrCandidate(value: unknown): value is OcrCandidate {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    isOcrCandidateField(candidate.field) &&
    typeof candidate.value === 'string' &&
    typeof candidate.confidence === 'number' &&
    typeof candidate.sourcePhotoId === 'string'
  );
}

function isOcrCandidateField(value: unknown): value is OcrCandidateField {
  return ocrCandidateFields.includes(value as OcrCandidateField);
}
